import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleContents } from '@/mocks/articleData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articleContents[slug] : undefined;

  if (!article) {
    return (
      <main>
        <Navbar />
        <article className="pt-32 pb-20 text-center">
          <div className="max-w-2xl mx-auto px-6">
            <h1 className="text-3xl font-heading font-bold text-foreground-950 mb-4">Artigo não encontrado</h1>
            <p className="text-foreground-600 mb-6">O artigo que você procura não está disponível.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer">
              <i className="ri-arrow-left-line"></i> Voltar ao Início
            </Link>
          </div>
        </article>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <Navbar />

      <article className="pt-24">
        {/* Hero */}
        <section className="relative bg-background-50 py-12 md:py-20">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <p className="text-xs font-semibold uppercase tracking-widest text-accent-600 mb-4">Artigo Hoobuy.homes</p>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 mt-6 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                Atualizado: Junho 2026
              </span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.readTime} de leitura
              </span>
              <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                Equipe Hoobuy.homes
              </span>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="relative bg-background-50 pb-8">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={article.title + " - Hoobuy Spreadsheet"}
              className="w-full rounded-3xl object-cover h-[250px] md:h-[380px]"
            />
          </div>
        </section>

        {/* Content */}
        <section className="relative bg-background-50 py-8 md:py-12">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="prose-custom text-foreground-800 leading-relaxed">

              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-10 flex-wrap" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Início</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-700 font-medium">{article.title}</span>
              </nav>

              {/* Sections */}
              {article.sections.map((section, idx) => (
                <div key={idx}>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-10 mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((p, pIdx) => (
                    <p key={pIdx} className="mb-4" dangerouslySetInnerHTML={{
                      __html: p.replace(
                        /\*\*(.*?)\*\*/g,
                        '<strong>$1</strong>'
                      ).replace(
                        /Hoobuy\.homes/g,
                        '<a href="/" class="text-primary-600 hover:text-primary-700 underline">Hoobuy.homes</a>'
                      ),
                    }} />
                  ))}
                </div>
              ))}

              {/* Internal CTA */}
              <div className="my-12 bg-primary-500 rounded-3xl p-8 md:p-10 text-center">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-background-50 mb-3">
                  Pronto para Explorar o Hoobuy Spreadsheet?
                </h2>
                <p className="text-background-50/80 text-sm mb-6 max-w-md mx-auto">
                  Acesse agora o maior <strong>Hoobuy Spreadsheet em português</strong> com mais de 20.000 produtos e links atualizados diariamente.
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50 text-primary-600 text-base font-semibold rounded-full hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  {article.ctaText}
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>

              {/* Related Articles */}
              <div className="border-t border-background-200 pt-10 mt-12">
                <h3 className="text-lg font-heading font-bold text-foreground-950 mb-6">Artigos Relacionados</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {article.relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/artigo/${ra.slug}`}
                      className="group bg-background-100 rounded-xl p-5 border border-background-200/70 hover:border-background-300 transition-all duration-200 cursor-pointer"
                    >
                      <p className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </p>
                      <p className="text-xs text-foreground-500 mt-1">
                        Leia mais sobre {ra.title.toLowerCase()}.
                      </p>
                    </Link>
                  ))}
                  <Link
                    to="/"
                    className="group bg-background-100 rounded-xl p-5 border border-background-200/70 hover:border-background-300 transition-all duration-200 cursor-pointer"
                  >
                    <p className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                      Início — Maior Hoobuy Spreadsheet em Português
                    </p>
                    <p className="text-xs text-foreground-500 mt-1">
                      Explore 14 categorias com links atualizados.
                    </p>
                  </Link>
                </div>
              </div>

              {/* Author Box */}
              <div className="mt-12 bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/70 flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-line text-background-50 text-xl"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-950">Equipe Hoobuy.homes</p>
                  <p className="text-xs text-foreground-500 mt-1 leading-relaxed">
                    Somos uma equipe dedicada a manter o <strong>Hoobuy Spreadsheet</strong> atualizado diariamente. Analisamos fornecedores, verificamos links e selecionamos os melhores produtos para os usuários do Brasil e de Portugal.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </article>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.description,
            image: article.imageUrl,
            author: {
              '@type': 'Organization',
              name: 'Hoobuy Homes',
            },
            datePublished: '2026-06-26',
            dateModified: '2026-06-26',
            publisher: {
              '@type': 'Organization',
              name: 'Hoobuy Homes',
            },
            inLanguage: 'pt-BR',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://hoobuy.homes/artigo/${article.slug}`,
            },
          }),
        }}
      />
    </main>
  );
}